import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button, useTheme, Avatar, Stack, Paper } from '@mui/material';
import { Agriculture, AdminPanelSettings, ArrowForward, VerifiedUser, Psychology, Public } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LandingSelection = () => {
    const navigate = useNavigate();

    const handleSelect = (role) => {
        localStorage.setItem('userRole', role);
        if (role === 'ganadero') {
            navigate('/ganadero');
        } else {
            navigate('/app/dashboard');
        }
    };

    return (
        <Box 
            sx={{ 
                minHeight: '100vh', 
                bgcolor: 'var(--bg-app)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, md: 4 },
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Background Decorative Element */}
            <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, var(--emerald-light) 0%, transparent 70%)', opacity: 0.1, pointerEvents: 'none' }} />
            <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, var(--navy-main) 0%, transparent 70%)', opacity: 0.05, pointerEvents: 'none' }} />

            <Container maxWidth="lg" sx={{ zIndex: 1, position: 'relative' }}>
                <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                        <Public sx={{ color: 'var(--emerald-main)', fontSize: 20 }} />
                        <Typography variant="overline" sx={{ fontWeight: 900, color: 'var(--slate-light)', letterSpacing: '0.2em' }}>
                            BIZKAIA GOVTECH ECOSYSTEM
                        </Typography>
                    </Stack>
                    <Typography 
                        variant="h1" 
                        sx={{ 
                            fontWeight: 950, 
                            color: 'var(--navy-main)',
                            letterSpacing: '-0.05em',
                            mb: 2,
                            fontSize: { xs: '3rem', md: '5rem' },
                            fontFamily: 'var(--font-header)',
                            textTransform: 'uppercase'
                        }}
                    >
                        BASERRI-ADITU
                    </Typography>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            color: 'var(--slate-light)', 
                            maxWidth: '800px', 
                            mx: 'auto',
                            fontWeight: 600,
                            lineHeight: 1.4,
                            px: 2
                        }}
                    >
                        La vanguardia en gestión ganadera cognitiva. 
                        Infraestructura digital Voice-First para la excelencia del sector primario.
                    </Typography>
                </Box>

                <Grid container spacing={4} justifyContent="center">
                    {/* Ganadero Path */}
                    <Grid item xs={12} md={5}>
                        <Card 
                            className="premium-card accent-emerald"
                            onClick={() => handleSelect('ganadero')}
                            sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                border: '1px solid var(--border-color)',
                                '&:hover': {
                                    transform: 'translateY(-12px)',
                                    boxShadow: 'var(--shadow-lg)',
                                    borderColor: 'var(--emerald-main)'
                                }
                            }}
                        >
                            <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                                <Avatar 
                                    sx={{ 
                                        width: 90, height: 90, 
                                        bgcolor: 'var(--emerald-main)', 
                                        color: 'white',
                                        mx: 'auto', mb: 4,
                                        boxShadow: '0 10px 20px rgba(16, 185, 129, 0.2)'
                                    }}
                                >
                                    <Agriculture sx={{ fontSize: 45 }} />
                                </Avatar>
                                <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, color: 'var(--navy-main)' }}>
                                    Portal Ganadero
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'var(--slate-light)', mb: 5, fontWeight: 500, minHeight: '4.5em' }}>
                                    Interacción natural por voz en campo. Sincronización REGA instantánea para partos, traslados y sanidad animal.
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    fullWidth
                                    endIcon={<ArrowForward />}
                                    sx={{ 
                                        borderRadius: '16px', 
                                        py: 2, 
                                        bgcolor: 'var(--emerald-main)',
                                        color: 'white',
                                        textTransform: 'none',
                                        fontSize: '1.2rem',
                                        fontWeight: 900,
                                        boxShadow: '0 8px 16px rgba(16, 185, 129, 0.2)',
                                        '&:hover': { bgcolor: 'var(--forest-main)' }
                                    }}
                                >
                                    Iniciar Asistente
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Administrador Path */}
                    <Grid item xs={12} md={5}>
                        <Card 
                            className="premium-card accent-navy"
                            onClick={() => handleSelect('admin')}
                            sx={{ 
                                height: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                border: '1px solid var(--border-color)',
                                '&:hover': {
                                    transform: 'translateY(-12px)',
                                    boxShadow: 'var(--shadow-lg)',
                                    borderColor: 'var(--navy-main)'
                                }
                            }}
                        >
                            <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                                <Avatar 
                                    sx={{ 
                                        width: 90, height: 90, 
                                        bgcolor: 'var(--navy-main)', 
                                        color: 'white',
                                        mx: 'auto', mb: 4,
                                        boxShadow: '0 10px 20px rgba(30, 58, 95, 0.2)'
                                    }}
                                >
                                    <AdminPanelSettings sx={{ fontSize: 45 }} />
                                </Avatar>
                                <Typography variant="h4" sx={{ fontWeight: 900, mb: 2, color: 'var(--navy-main)' }}>
                                    Control Regional
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'var(--slate-light)', mb: 5, fontWeight: 500, minHeight: '4.5em' }}>
                                    Supervisión institucional y cumplimiento. Auditoría de flujos blockchain y monitorización de alertas sanitarias.
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    fullWidth
                                    endIcon={<ArrowForward />}
                                    sx={{ 
                                        borderRadius: '16px', 
                                        py: 2, 
                                        borderColor: 'var(--navy-main)',
                                        color: 'var(--navy-main)',
                                        borderWidth: 2,
                                        textTransform: 'none',
                                        fontSize: '1.2rem',
                                        fontWeight: 900,
                                        '&:hover': {
                                            borderWidth: 2,
                                            background: '#f1f5f9'
                                        }
                                    }}
                                >
                                    Consola de Mando
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 10, textAlign: 'center' }}>
                    <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <VerifiedUser sx={{ color: 'var(--emerald-main)', fontSize: 16 }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)' }}>SISTEMA CERTIFICADO</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Psychology sx={{ color: 'var(--navy-main)', fontSize: 16 }} />
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)' }}>IA COGNITIVA ACTIVA</Typography>
                        </Box>
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'var(--slate-light)', fontSize: '0.8rem', fontWeight: 600, opacity: 0.6 }}>
                        Plataforma Oficial Baserrri-Aditu | © 2026 Gobierno Vasco - Lantic GovTech
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default LandingSelection;
